import { BlockTransitionContext, IBlockTransitionContext } from 'browser/components/atomic-elements/atoms/navigation/block-transition-context'
import PropTypes from 'prop-types'
import React from 'react'
import { withContext } from 'shared-libs/components/context/with-context'

interface IBlockTransitionProps {
  children?: React.ReactNode
  condition: boolean
  onBlockTransition: (callback: any) => void
  debug: string
}


interface ContextProps {
  blockTransitionContext?: IBlockTransitionContext
}

interface RouterContext {
  router: {
    history: {
      block: (fn: any) => () => void
      push: (location: any) => void
    }
  }
}

const LOCAL_DEBUG = false // leaving this in just in-case, it's a huge pain to debug this style of component without having contextual debug logs

/*
  We use BlockTransitionContext to ensure that only one callback is active at a time via BlockTransition
  When a BlockTransition is nested within a BlockTransition, the outer component will disable itself to allow the inner one to function without contention
  Behaviour is still undefined when there are two side-by-side BlockTransition's in the component tree, and there's no reasonable way to prioritize one over another
*/
@withContext(BlockTransitionContext, 'blockTransitionContext')
export class BlockTransition extends React.PureComponent<IBlockTransitionProps & ContextProps, any> {
  public static contextTypes = {
    router: PropTypes.shape({
      history: PropTypes.shape({
        block: PropTypes.func,
      }).isRequired,
    }).isRequired,
  }

  declare context: RouterContext

  private unblock: any

  private childCount: number
  private isEnabled: boolean
  private _isMounted: boolean

  constructor(props) {
    super(props)

    this.childCount = 0
    this.isEnabled = false
  }

  public UNSAFE_componentWillMount() {
    this._isMounted = true
    this.props.blockTransitionContext?.addChild?.()

    if (this.props.condition) {
      this.enable(this.props)
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.condition) {
      this.enable(nextProps)
    } else {
      this.disable()
    }
  }

  public componentWillUnmount() {
    this.props.blockTransitionContext?.removeChild?.()
    this.disable()
    this._isMounted = false
  }

  public render() {
    if (!this.props.children) {
      return null
    }

    const contextProps = {
      addChild: this.addChild,
      removeChild: this.removeChild,
    }

    return <BlockTransitionContext.Provider value={contextProps}>
      {this.props.children}
    </BlockTransitionContext.Provider>
  }

  private addChild = () => {
    this.childCount += 1
    this.disable()
  }

  private removeChild = () => {
    this.childCount -= 1

    if (this.props.condition) {
      this.enable(this.props)
    }
  }

  private enable = (props: IBlockTransitionProps) => {
    if (this.childCount > 0 || this.isEnabled || !this._isMounted) {
      return
    }

    this.isEnabled = true
    this.unblock = this.context.router.history.block((location, action) => {
      if (action === 'PUSH') {
        props.onBlockTransition(() => {
          this.disable()
          this.context.router.history.push(location)
        })
      }
      return false
    })
  }

  private disable = () => {
    if (!this.isEnabled) {
      return
    }

    this.isEnabled = false
    this.unblock?.()
    this.unblock = null
  }
}
